/**
 * EmployeeRoster — Displays active staff with contact info on the Home page.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import * as ExcelJS from 'exceljs';
import { Download, Loader2, Search, Users, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { useAuth } from '@/contexts/useAuth';
import {
  ROSTER_TEAMS,
  createRosterGroups,
  flattenRosterGroups,
  getRosterTeam,
  type RosterTeam,
  type RosterTeamFilter
} from '@/components/home/rosterGrouping';
import { getOrganizationRoster } from '@/services/userService';
import type { RosterUser } from '@/types/user';
import { createComponentLogger } from '@/utils/logger';
import { formatPhoneNumber } from '@/utils/phoneFormatting';

const log = createComponentLogger('EmployeeRoster', 'dashboard');
const PREVIEW_COUNT = 8;
const EMPTY_VALUE = '—';

function getPosition(user: RosterUser): string {
  return user.title ?? '';
}

function getPhone(user: RosterUser): string {
  const raw = user.phone || user.mobilePhone;
  if (!raw) {
    return EMPTY_VALUE;
  }
  return formatPhoneNumber(raw) || EMPTY_VALUE;
}

function getTwilioNumber(user: RosterUser): string {
  if (user.twilioNumberStatus === 'active' && user.businessSmsNumber) {
    return formatPhoneNumber(user.businessSmsNumber) || user.businessSmsNumber;
  }
  return EMPTY_VALUE;
}

function getFullName(user: RosterUser): string {
  return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
}

function matchesRosterSearch(user: RosterUser, query: string): boolean {
  const searchableValues = [
    getFullName(user),
    user.email,
    getPhone(user),
    getTwilioNumber(user),
    getPosition(user),
    user.branchName
  ];
  return searchableValues.some((value) => (
    (value ?? '').toLocaleLowerCase().includes(query)
  ));
}

interface EmployeeRosterProps {
  className?: string;
}

export function EmployeeRoster({ className }: EmployeeRosterProps = {}) {
  const { user, session } = useAuth();
  // The RPC derives the organization from the caller's session, so this is a
  // readiness gate rather than a query argument: it keeps the roster from
  // firing before auth hydrates, and losing auth flips it back to undefined so
  // the effect below aborts whatever request is still in flight.
  const organizationId = (
    user
    && session?.access_token
    && session.user?.id === user.id
  )
    ? user.organization_id
    : undefined;
  const [users, setUsers] = useState<RosterUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState<RosterTeamFilter>('All');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!organizationId) {
      setUsers([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);

    async function fetchRoster() {
      const { data, error } = await getOrganizationRoster({
        signal: controller.signal
      });
      if (cancelled || controller.signal.aborted) {
        return;
      }
      if (error) {
        log.error('Failed to fetch employee roster', { error });
      }
      setUsers(data);
      setLoading(false);
    }

    void fetchRoster();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [organizationId]);

  const groups = useMemo(() => createRosterGroups(users), [users]);
  const allUsers = useMemo(() => flattenRosterGroups(groups), [groups]);
  const selectedTeamUsers = useMemo(() => (
    teamFilter === 'All' ? allUsers : groups[teamFilter]
  ), [allUsers, groups, teamFilter]);
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const searching = normalizedSearch.length > 0;
  const matchedUsers = useMemo(() => {
    if (!searching) {
      return selectedTeamUsers;
    }
    return selectedTeamUsers.filter((rosterUser) => (
      matchesRosterSearch(rosterUser, normalizedSearch)
    ));
  }, [normalizedSearch, searching, selectedTeamUsers]);
  const rosterOpen = searching || (
    expanded && matchedUsers.length > PREVIEW_COUNT
  );
  const visibleUsers = rosterOpen
    ? matchedUsers
    : matchedUsers.slice(0, PREVIEW_COUNT);
  const visibleUserIds = useMemo(
    () => new Set(visibleUsers.map((rosterUser) => rosterUser.id)),
    [visibleUsers]
  );

  const rosterMeta = useMemo(() => {
    if (searching) {
      return `${matchedUsers.length} ${matchedUsers.length === 1 ? 'match' : 'matches'}`;
    }
    if (teamFilter !== 'All') {
      return `${matchedUsers.length} in ${teamFilter}`;
    }
    return `${allUsers.length} ${allUsers.length === 1 ? 'person' : 'people'}`;
  }, [allUsers.length, matchedUsers.length, searching, teamFilter]);

  const exportToExcel = useCallback(async () => {
    if (matchedUsers.length === 0) {
      return;
    }
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Employee Roster');

    const headers = ['Name', 'Email', 'Phone', 'Twilio SMS', 'Position'];
    const headerRow = sheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF2563EB' }
      };
      cell.alignment = { vertical: 'middle' };
    });

    for (const rosterUser of matchedUsers) {
      sheet.addRow([
        getFullName(rosterUser),
        rosterUser.email || EMPTY_VALUE,
        getPhone(rosterUser),
        getTwilioNumber(rosterUser),
        getPosition(rosterUser)
      ]);
    }

    sheet.columns.forEach((column) => {
      let max = 12;
      column.eachCell?.({ includeEmpty: false }, (cell) => {
        const length = String(cell.value ?? '').length;
        if (length > max) {
          max = length;
        }
      });
      column.width = Math.min(max + 2, 40);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('.')[0];
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `employee-roster_${timestamp}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [matchedUsers]);

  const selectTeam = (team: RosterTeamFilter) => {
    setTeamFilter(team);
    setExpanded(false);
  };

  const renderEmployeeRow = (rosterUser: RosterUser, team: RosterTeam) => (
    <TableRow
      key={rosterUser.id}
      data-roster-employee-row={rosterUser.id}
      data-roster-team={team}
    >
      <TableCell>{getFullName(rosterUser)}</TableCell>
      <TableCell>{rosterUser.email}</TableCell>
      <TableCell className="lw-employee-roster__mono">
        {getPhone(rosterUser)}
      </TableCell>
      <TableCell className="lw-employee-roster__mono">
        {getTwilioNumber(rosterUser) !== EMPTY_VALUE ? (
          <span className="lw-employee-roster__twilio">
            <span aria-hidden="true" />
            {getTwilioNumber(rosterUser)}
          </span>
        ) : (
          <span className="lw-employee-roster__empty-value">{EMPTY_VALUE}</span>
        )}
      </TableCell>
      <TableCell>{getPosition(rosterUser)}</TableCell>
    </TableRow>
  );

  const renderRosterRows = () => {
    if (teamFilter !== 'All' || searching) {
      const team = teamFilter === 'All'
        ? undefined
        : teamFilter;
      return visibleUsers.map((rosterUser) => (
        renderEmployeeRow(
          rosterUser,
          team ?? getRosterTeam(rosterUser)
        )
      ));
    }

    return ROSTER_TEAMS.map((team) => {
      const teamUsers = groups[team].filter((rosterUser) => (
        visibleUserIds.has(rosterUser.id)
      ));
      if (teamUsers.length === 0) {
        return null;
      }
      return (
        <Fragment key={team}>
          <TableRow
            className="lw-employee-roster__group-row"
            data-roster-group-header={team}
          >
            <TableCell colSpan={5}>
              {team} · {groups[team].length}
            </TableCell>
          </TableRow>
          {teamUsers.map((rosterUser) => renderEmployeeRow(rosterUser, team))}
        </Fragment>
      );
    });
  };

  const footerCopy = searching
    ? `Searching all ${selectedTeamUsers.length} — name, email, phone, position or branch`
    : rosterOpen
      ? 'Scroll within the list · headers stay pinned'
      : matchedUsers.length > PREVIEW_COUNT
        ? `Showing the first ${PREVIEW_COUNT} of ${matchedUsers.length}`
        : `Showing all ${matchedUsers.length}`;
  const reservesExpandedHeight = !searching
    && matchedUsers.length > PREVIEW_COUNT;
  const tableContainerProps = {
    'aria-label': 'Employee roster table',
    'data-expanded': rosterOpen ? 'true' : 'false',
    className: [
      'lw-employee-roster__scroll-region',
      reservesExpandedHeight ? 'has-more' : '',
      rosterOpen ? 'is-open' : ''
    ].filter(Boolean).join(' '),
    id: 'employee-roster-table',
    role: 'region',
    tabIndex: 0
  };

  return (
    <div className={className}>
      <CardHeader>
        <div className="lw-employee-roster__header">
          <div className="lw-employee-roster__heading">
            <CardTitle>Employee Roster</CardTitle>
            {!loading && allUsers.length > 0 && (
              <p className="lw-employee-roster__meta" aria-live="polite">
                {!searching && teamFilter === 'All' && (
                  <>Clustered by team, then seniority · </>
                )}
                <strong>{rosterMeta}</strong>
              </p>
            )}
            {!loading && allUsers.length > 0 && (
              <span aria-hidden="true" />
            )}
          </div>
          {!loading && allUsers.length > 0 && (
            <div className="lw-employee-roster__actions">
              <div className="lw-employee-roster__search">
                <Search aria-hidden="true" />
                <Input
                  aria-label="Search employee roster"
                  className={search ? 'has-query' : undefined}
                  placeholder={`Search all ${selectedTeamUsers.length} — name, email, position…`}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                {search && (
                  <button
                    type="button"
                    aria-label="Clear roster search"
                    onClick={() => setSearch('')}
                  >
                    <X aria-hidden="true" />
                  </button>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={exportToExcel}
                disabled={matchedUsers.length === 0}
              >
                <Download aria-hidden="true" />
                Export
              </Button>
            </div>
          )}
        </div>
        {!loading && allUsers.length > 0 && (
          <div
            className="lw-employee-roster__teams"
            role="group"
            aria-label="Filter employee roster by team"
          >
            <button
              type="button"
              aria-pressed={teamFilter === 'All'}
              onClick={() => selectTeam('All')}
            >
              All teams <span>{allUsers.length}</span>
            </button>
            {ROSTER_TEAMS.map((team) => (
              <button
                type="button"
                aria-pressed={teamFilter === team}
                key={team}
                onClick={() => selectTeam(team)}
              >
                {team} <span>{groups[team].length}</span>
              </button>
            ))}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="lw-employee-roster__state">
            <Loader2 className="animate-spin" aria-hidden="true" />
            Loading roster…
          </div>
        ) : allUsers.length === 0 ? (
          <div className="lw-employee-roster__state">
            <Users aria-hidden="true" />
            <p>No active employees found</p>
          </div>
        ) : (
          <div className="lw-employee-roster__table-wrap">
            <Table
              containerProps={tableContainerProps}
            >
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Twilio SMS</TableHead>
                  <TableHead>Position</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {matchedUsers.length === 0 ? (
                  <TableRow className="lw-employee-roster__empty-row">
                    <TableCell colSpan={5}>
                      {searching ? (
                        <>
                          <p>No employees match &ldquo;{search.trim()}&rdquo;</p>
                          <button type="button" onClick={() => setSearch('')}>
                            Clear search
                          </button>
                        </>
                      ) : (
                        <p>No active employees in {teamFilter}</p>
                      )}
                    </TableCell>
                  </TableRow>
                ) : renderRosterRows()}
              </TableBody>
            </Table>
            <div className="lw-employee-roster__footer">
              <span aria-live="polite">{footerCopy}</span>
              {!searching && matchedUsers.length > PREVIEW_COUNT && (
                <button
                  type="button"
                  aria-controls="employee-roster-table"
                  aria-expanded={expanded}
                  onClick={() => setExpanded((current) => !current)}
                >
                  {expanded
                    ? `Show first ${PREVIEW_COUNT}`
                    : `Show all ${matchedUsers.length} ↓`}
                </button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </div>
  );
}
